import React, { useState, useEffect } from 'react';
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import DateFnsUtils from '@date-io/date-fns';
import { ThemeProvider } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { IntlProvider } from 'react-intl';
import { BrowserRouter, Route } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import getNameFromToken from '../src/util/jwtToken';
import './App.css';
import MainLayout from './components/MainLayout.jsx';
import HomePage from './pages/HomePage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import CardholdersPage from './pages/CardholdersPage.jsx';
import AdminPage from './pages/AdminPage.jsx';
import ReportsPage from './pages/ReportsPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import CardholderEditPage from './pages/CardholderEditPage.jsx';
import CardholderFields from './pages/CardholderFields.jsx';
import GlobalSettings from './pages/GlobalSettings.jsx';
import PhotoRecall from './components/PhotoRecall.jsx';
import CardFormats from './pages/CardFormats.jsx';
import LightTheme from './themes/light-theme';
import DarkTheme from './themes/dark-theme';
import useLocalStorage from './util/useLocalStorage';
import { getMessages, getLocale } from './util/languageHelpers';
import DoorControlView from './components/DoorControlView.jsx';
import GlobalScheduler from './pages/GlobalScheduler.jsx';
import SiteStateContext from './util/SiteStateContext.jsx';
import MobileCredentialPage from './pages/MobileCredentialPage.jsx';
import MobileCredentialAuthPage from './pages/MobileCredentialAuthPage.jsx';
import PhotoRecallWidget from './components/PhotoRecallWidget.jsx';
import UserConfig from './pages/UserConfig.jsx';

import { GET_USER_CONFIG } from '../src/util/schema.js';

function App() {
  const [apiToken, setApiToken] = useLocalStorage('token', null);
  const [colorTheme, setColorTheme] = useLocalStorage('theme', 'light');
  const [mode, setMode] = useState();
  const [filters, setFilters] = useState([]);

  const [cookies, setCookie, removeCookie] = useCookies(['name']);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [siteState, setSiteState] = useState({ bookmarksOpened: false });
  const locale = getLocale();
  const messages = getMessages();
  const { activeSession } = sessionStorage;
  const setBookmarksOpened = () => {
    const updatedState = siteState;
    updatedState.bookmarksOpened = true;
    setSiteState(updatedState);
  };
  window.onload = () => {
    if (sessionStorage.loggedOutOnAuth) {
      console.log('Logged out due to expiry already');
    } else if (!activeSession) {
      sessionStorage.loggedOutOnAuth = true;
      setApiToken(null);
    }
  };

  const setUserConfig = (token) => {
    setApiToken(token);
  };

  return (
    <ThemeProvider theme={colorTheme === 'dark' ? DarkTheme : LightTheme}>
      <IntlProvider messages={messages} locale={locale} defaultLocale="en">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <BrowserRouter>
            <SiteStateContext.Provider value={siteState}>
              {apiToken === null && (
                <LoginPage
                  setMode={setMode}
                  setFilters={setFilters}
                  setApiToken={setUserConfig}
                  setColorTheme={setColorTheme}
                  apiToken={apiToken}
                />
              )}
              {apiToken !== null && (
                <MainLayout
                  apiToken={apiToken}
                  setApiToken={setApiToken}
                  setColorTheme={setColorTheme}
                  isMobile={isMobile}
                  mode={mode}
                  filters={filters}
                >
                  <Route
                    path="/"
                    exact
                    render={(props) => (
                      <CardholdersPage {...props} apiToken={apiToken} filters={filters} />
                    )}
                  />
                  <Route path="/homepage" exact component={HomePage} />
                  <Route
                    path="/dashboard"
                    render={() => <DashboardPage setBookmarksOpened={setBookmarksOpened} />}
                  />
                  <Route path="/doorcontrol" component={DoorControlView} />
                  <Route
                    path="/cardholders"
                    exact
                    render={(props) => (
                      <CardholdersPage {...props} apiToken={apiToken} filters={filters} />
                    )}
                  />
                  <Route path="/admin" component={AdminPage} />
                  <Route path="/reports" component={ReportsPage} />
                  <Route path="/settings" component={SettingsPage} />
                  <Route path="/photorecall" component={PhotoRecall} />
                  <Route
                    path="/cardholders/:id"
                    render={(props) => <CardholderEditPage {...props} isMobile={isMobile} />}
                  />
                  <Route path="/cardholderfields" component={CardholderFields} />
                  <Route path="/scheduler" component={GlobalScheduler} />
                  <Route path="/globalsettings/:id" component={GlobalSettings} />
                  <Route path="/cardformats" component={CardFormats} />
                  <Route path="/mobilecredential" exact component={MobileCredentialPage} />
                  <Route path="/mobilecredential/auth" component={MobileCredentialAuthPage} />
                  <Route path="/photorecallwidget" component={PhotoRecallWidget} />
                  <Route
                    path="/userconfig"
                    render={(props) => (
                      <UserConfig {...props} apiToken={apiToken} setColorTheme={setColorTheme} />
                    )}
                  />
                </MainLayout>
              )}
            </SiteStateContext.Provider>
          </BrowserRouter>
        </MuiPickersUtilsProvider>
      </IntlProvider>
    </ThemeProvider>
  );
}

export default App;
